import { supabase } from '../../../lib/supabase';
import { normalizeGoogleEventId } from '../../../lib/utils/googleEventId';
import { useActivityTypesStore } from '../../../lib/stores/activityTypesStore';
import { buildAttendees, TYPES_WITH_ATTENDEES } from './eventData';
import type { CalendarSyncResult, ResolvedSyncSettings } from './types';

/**
 * Helper puri/di risoluzione per `useActivitySave.ts`, estratti per rispettare il
 * budget di righe del task (>350 → estrarre `activityPayload.ts`, spec Task 8).
 * Nessuna chiamata alle API calendario/AlfaDocs qui: solo costruzione payload/ctx.
 */

/** Fonte: LeadStatusUpdateDrawer.tsx:1082. */
const DB_VALID_TYPES = ['call', 'videochiamata', 'appuntamento', 'sopralluogo', 'email', 'task', 'note', 'other'];

/**
 * Combina gli esiti Google+Microsoft in UN SOLO patch per l'update finale su
 * `sales_activities` (spec §"UN unico update finale"). Pura, testata a parte.
 *
 * `clearRefs` ha priorità sullo status: nei moduli reali (`googleSync.ts`/
 * `microsoftSync.ts`) accompagna sempre `status: 'skipped'`, mai `'ok'`, ma la
 * funzione resta corretta anche nel caso ipotetico di entrambi valorizzati.
 *
 * Fix round 1 del redesign drawer: `google.clearRefs` azzera ANCHE `meet_link`. Il Meet
 * vive dentro l'evento Google che stiamo cancellando: lasciarlo persistito significa
 * mostrare in scheda un link a una riunione che non esiste più. Se nello stesso giro
 * l'altro provider produce un nuovo link (move verso Teams), il blocco `meetLink` sotto
 * sovrascrive il null — l'ordine delle assegnazioni è deliberato.
 */
export function buildFinalPatch(google: CalendarSyncResult, microsoft: CalendarSyncResult): Record<string, any> {
  const patch: Record<string, any> = {};

  if (google.clearRefs) {
    patch.google_event_id = null;
    patch.calendar_id = null;
    patch.meet_link = null;
  } else if (google.status === 'ok') {
    patch.google_event_id = normalizeGoogleEventId(google.eventId);
    patch.calendar_id = google.calendarId;
  }

  if (microsoft.clearRefs) {
    patch.microsoft_event_id = null;
  } else if (microsoft.status === 'ok') {
    patch.microsoft_event_id = microsoft.eventId;
  }

  const meetLink = google.meetLink ?? microsoft.meetLink ?? null;
  if (meetLink) {
    patch.meet_link = meetLink;
  }

  return patch;
}

/**
 * Validazione tipo prima del salvataggio (safety net contro valori invalidi da
 * localStorage). Fonte: LeadStatusUpdateDrawer.tsx:1081-1102.
 *
 * Convenzione canonica AlfaDocs (Task 16, spec §6.5): un tipo RAW `alfadocs_*` (dalla
 * select "Tipo", v. `useDrawerData.buildAlfaDocsTypeGroup`) NON viene più persistito
 * così com'è su `sales_activities.type` — si traduce SEMPRE al tipo standard
 * `'appuntamento'`, come faceva il vecchio drawer DEAL pre-refactor
 * (`StatusUpdateDrawer.tsx`, stato `alfaSubType` separato dal `type`). Il sottotipo
 * (`visita`/`operazione`/`telehealth`) NON si perde: vive in `metadata.alfadocs_type`,
 * scritto da `syncAlfaDocsAppointment` (v. `useAlfaDocsSync.ts`) a partire dal tipo RAW
 * risolto da `resolveAlfaDocsSubType` — chiamata dal chiamante PRIMA di questa funzione,
 * sullo stesso `rawType`, perché qui l'informazione va perduta.
 */
export async function resolveSafeType(rawType: string): Promise<string> {
  const mappedType = rawType === 'meeting' ? 'videochiamata' : rawType;
  if (typeof mappedType === 'string' && mappedType.startsWith('alfadocs_')) return 'appuntamento';
  if (!useActivityTypesStore.getState().fetched) {
    try {
      await useActivityTypesStore.getState().fetchCustomTypes();
    } catch (err) {
      console.error('[useActivitySave] fetchCustomTypes prima della validazione tipo:', err);
    }
  }
  const isCustomType = useActivityTypesStore.getState().customTypes.some((ct) => ct.slug === mappedType);
  return isCustomType ? mappedType : DB_VALID_TYPES.includes(mappedType) ? mappedType : 'task';
}

/**
 * Sottotipo AlfaDocs (`visita`, `operazione`, `telehealth`, ...) dal tipo RAW scelto in UI
 * (`alfadocs_visita` ecc.). `null` per qualunque altro tipo — l'attività non è un
 * appuntamento AlfaDocs. Pura, testata a parte (v. `resolveSafeType`, la traduzione
 * simmetrica verso il tipo canonico persistito).
 *
 * Retrocompat in LETTURA (Task 16): applicata anche al `type` GIÀ PERSISTITO di
 * un'attività in editing per derivare un fallback quando `metadata.alfadocs_type`
 * fosse assente su righe storiche in convenzione LEAD (`type='alfadocs_visita'`) — v.
 * `syncAlfaDocsUpdate` in `useActivitySave.ts`.
 */
export function resolveAlfaDocsSubType(rawType: string | null | undefined): string | null {
  return typeof rawType === 'string' && rawType.startsWith('alfadocs_')
    ? rawType.slice('alfadocs_'.length)
    : null;
}

/** Fonte: LeadStatusUpdateDrawer.tsx:1195-1199/:1559-1563 — pattern ripetuto, qui in un solo posto. */
export async function resolveEmail(userId: string | null | undefined): Promise<string | null> {
  if (!userId) return null;
  const { data } = await supabase.from('user_profiles').select('email').eq('id', userId).single();
  return data?.email || null;
}

/**
 * Demozione del falso successo (CRITICAL, review post-Task 8): `syncGoogleEvent`/
 * `syncMicrosoftEvent` possono ritornare `status:'ok'` con `eventId` falsy — capita
 * perché `useGoogleCalendar.createEvent`/`useMicrosoftCalendar.createEvent` NON
 * lanciano mai (inghiottono l'errore e ritornano `null`), quindi il modulo di sync
 * vede un `created` falsy senza mai entrare nel proprio `catch`.
 *
 * Si applica SEMPRE, sia insert che update: il ramo "create" di `syncGoogleEvent`
 * è raggiungibile anche in update, quando l'attività non ha ancora un evento
 * oppure il ref esistente è incompleto (`canMove` false, es. `calendar_id` null) —
 * demotare solo su insert (prima versione di questo hook) lasciava scrivere
 * `google_event_id: null` sopra un ref persistito valido in quei casi update.
 * Mai patchare i ref quando il risultato è un falso successo.
 */
export function demoteFalseSuccess(r: CalendarSyncResult): CalendarSyncResult {
  return r.status === 'ok' && !r.eventId
    ? { ...r, status: 'error', error: r.error || 'sync ok ma eventId mancante' }
    : r;
}

export interface AttendeeSets {
  updateAttendees: string[];
  insertGoogleAttendees: string[];
  insertMicrosoftAttendees: string[];
}

/**
 * Risolve le email attendee per branch (decisione #2 del ledger, dal docstring di
 * `buildAttendees`): insert Google = selected+currentUser+assignee+invitedCommercial;
 * UPDATE (Google e Microsoft) = selected+currentUser+assignee, SENZA invitedCommercial;
 * insert Microsoft = SOLO selected+currentUser.
 *
 * Il dedup di `buildAttendees` rende innocuo risolvere assigneeEmail/invitedCommercialEmail
 * anche quando coincidono col currentUser/organizer — nessuna voce duplicata in output.
 */
export async function buildActivityAttendees(params: {
  safeType: string;
  selectedEmails: string[];
  currentUserEmail: string | null;
  finalAssignedTo: string | null;
  invitedCommercial: string | null;
  isInsert: boolean;
}): Promise<AttendeeSets> {
  const { safeType, selectedEmails, currentUserEmail, finalAssignedTo, invitedCommercial, isInsert } = params;
  const isAttendeeType = (TYPES_WITH_ATTENDEES as readonly string[]).includes(safeType);

  let assigneeEmail: string | null = null;
  let invitedCommercialEmail: string | null = null;
  if (isAttendeeType) {
    assigneeEmail = await resolveEmail(finalAssignedTo);
    if (isInsert && invitedCommercial) {
      invitedCommercialEmail = await resolveEmail(invitedCommercial);
    }
  }

  return {
    updateAttendees: buildAttendees({ type: safeType, selected: selectedEmails, currentUserEmail, assigneeEmail, invitedCommercialEmail: null }),
    insertGoogleAttendees: buildAttendees({ type: safeType, selected: selectedEmails, currentUserEmail, assigneeEmail, invitedCommercialEmail }),
    insertMicrosoftAttendees: buildAttendees({ type: safeType, selected: selectedEmails, currentUserEmail, assigneeEmail: null, invitedCommercialEmail: null }),
  };
}

/**
 * Organizer Google in INSERT: swap sul commerciale invitato SOLO per i tipi con
 * attendee, con `meetOrganizer==='commercial'`, un commerciale diverso
 * dall'assegnatario e col proprio calendario configurato (fonte :1520-1546).
 * In update non c'è swap organizer (fonte :1170-1285) — non richiamare per quel ramo.
 */
export async function resolveGoogleOrganizer(params: {
  safeType: string;
  finalAssignedTo: string | null;
  currentUserId: string | null;
  meetOrganizer: 'agent' | 'commercial';
  invitedCommercial: string | null;
  googleSettings: ResolvedSyncSettings['google'];
}): Promise<{ userId: string; calendarId: string } | null> {
  const { safeType, finalAssignedTo, currentUserId, meetOrganizer, invitedCommercial, googleSettings } = params;
  const isAttendeeType = (TYPES_WITH_ATTENDEES as readonly string[]).includes(safeType);

  let creatorUserId = finalAssignedTo || currentUserId || '';
  let calendarToUse = googleSettings.calendarId;

  if (isAttendeeType && meetOrganizer === 'commercial' && invitedCommercial && invitedCommercial !== finalAssignedTo) {
    const { data: commercialProfile } = await supabase
      .from('user_profiles')
      .select('sync_activities_calendar_id')
      .eq('id', invitedCommercial)
      .single();
    if (commercialProfile?.sync_activities_calendar_id) {
      creatorUserId = invitedCommercial;
      calendarToUse = commercialProfile.sync_activities_calendar_id;
    }
  }

  return creatorUserId && calendarToUse ? { userId: creatorUserId, calendarId: calendarToUse } : null;
}
